import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewCoursesReportModal = ({ 
  isOpen, 
  onClose, 
  campusId,
  courseIds, 
  sessionStart,
  sessionEnd,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy data for courses report
  const dummyCoursesReport = {
    courses: [
      {
        id: "course1",
        name: "Mathematics Grade 5",
        code: "MATH-5",
        studentsRegistered: 45,
        teachersRegistered: 3,
        progressPercentage: 78.5,
        coursePrice: 15000,
        discountOffered: 2000,
        paymentReceived: 585000, // (45 * (15000 - 2000))
        completedStudents: 35,
        certificatesIssued: 35,
        status: "active"
      },
      {
        id: "course2",
        name: "English Grade 5",
        code: "ENG-5",
        studentsRegistered: 52,
        teachersRegistered: 4,
        progressPercentage: 85.2,
        coursePrice: 12000,
        discountOffered: 1500,
        paymentReceived: 546000, // (52 * (12000 - 1500))
        completedStudents: 44,
        certificatesIssued: 44,
        status: "active"
      },
      {
        id: "course3",
        name: "Science Grade 5",
        code: "SCI-5",
        studentsRegistered: 38,
        teachersRegistered: 2,
        progressPercentage: 92.3,
        coursePrice: 18000,
        discountOffered: 3000,
        paymentReceived: 570000, // (38 * (18000 - 3000))
        completedStudents: 35,
        certificatesIssued: 35,
        status: "active"
      },
      {
        id: "course4",
        name: "Social Studies Grade 5",
        code: "SS-5",
        studentsRegistered: 41,
        teachersRegistered: 2,
        progressPercentage: 67.8,
        coursePrice: 10000,
        discountOffered: 1000,
        paymentReceived: 369000, // (41 * (10000 - 1000))
        completedStudents: 28,
        certificatesIssued: 28,
        status: "active"
      },
      {
        id: "course5",
        name: "Computer Science",
        code: "CS-101",
        studentsRegistered: 29,
        teachersRegistered: 2,
        progressPercentage: 73.4,
        coursePrice: 25000,
        discountOffered: 5000,
        paymentReceived: 580000, // (29 * (25000 - 5000))
        completedStudents: 21,
        certificatesIssued: 21,
        status: "active"
      }
    ],
    summary: {
      totalStudents: 205,
      totalTeachers: 13,
      totalPaymentReceived: 2650000,
      totalCompletedCourses: 5,
      totalCertificatesIssued: 163,
      averageProgress: 79.44
    }
  };

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const downloadPDF = () => {
    generatePDFFromElement(reportRef, "Courses_Report.pdf");
  };

  const downloadExcel = () => {
    if (!dummyCoursesReport) return;

    const worksheetData = [
      ["Courses Report"],
      ["Campus", campusName],
      ["Session Period", `${sessionStart} to ${sessionEnd}`],
      ["Generated On", new Date().toLocaleDateString('en-GB')],
      [],
      ["Summary"],
      ["Total Students Registered", dummyCoursesReport.summary.totalStudents],
      ["Total Teachers Registered", dummyCoursesReport.summary.totalTeachers],
      ["Total Payment Received (PKR)", dummyCoursesReport.summary.totalPaymentReceived.toLocaleString()],
      ["Total Completed Courses", dummyCoursesReport.summary.totalCompletedCourses],
      ["Total Certificates Issued", dummyCoursesReport.summary.totalCertificatesIssued],
      ["Average Course Progress (%)", `${dummyCoursesReport.summary.averageProgress}%`],
      [],
      ["Course Name", "Course Code", "Students Registered", "Teachers Registered", "Progress (%)", "Course Price (PKR)", "Discount (PKR)", "Payment Received (PKR)", "Completed Students", "Certificates Issued"],
    ];

    if (dummyCoursesReport.courses && Array.isArray(dummyCoursesReport.courses)) {
      worksheetData.push(
        ...dummyCoursesReport.courses.map((course) => [
          course.name,
          course.code,
          course.studentsRegistered,
          course.teachersRegistered,
          `${course.progressPercentage}%`,
          course.coursePrice.toLocaleString(),
          course.discountOffered.toLocaleString(),
          course.paymentReceived.toLocaleString(),
          course.completedStudents,
          course.certificatesIssued
        ])
      );
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Courses Report");
    XLSX.writeFile(workbook, "Courses_Report.xlsx");
  };

  if (!isOpen) return null;

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600 bg-green-50';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatCurrency = (amount) => {
    return `PKR ${amount.toLocaleString()}`;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading courses report...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">Courses Report</h2>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div><strong>Session Period:</strong> {sessionStart} to {sessionEnd}</div>
                <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
                <div><strong>Number of Courses:</strong> {dummyCoursesReport.courses.length}</div>
                <div><strong>Report Type:</strong> Comprehensive Courses Analysis</div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Executive Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {dummyCoursesReport.summary.totalStudents}
                  </div>
                  <div className="text-sm text-gray-600">Total Students Registered</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {dummyCoursesReport.summary.totalTeachers}
                  </div>
                  <div className="text-sm text-gray-600">Total Teachers</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">
                    {formatCurrency(dummyCoursesReport.summary.totalPaymentReceived)}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="text-2xl font-bold text-orange-600">
                    {dummyCoursesReport.summary.totalCertificatesIssued}
                  </div>
                  <div className="text-sm text-gray-600">Certificates Issued</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    {dummyCoursesReport.summary.averageProgress.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600">Average Progress</div>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <div className="text-2xl font-bold text-indigo-600">
                    {dummyCoursesReport.summary.totalCompletedCourses}
                  </div>
                  <div className="text-sm text-gray-600">Active Courses</div>
                </div>
              </div>
            </div>

            {/* Detailed Courses Table */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Detailed Course Analysis</h3>
              {dummyCoursesReport.courses && dummyCoursesReport.courses.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-3 py-3 text-left font-medium">Course Name</th>
                        <th className="border border-gray-300 px-3 py-3 text-left font-medium">Course Code</th>
                        <th className="border border-gray-300 px-3 py-3 text-center font-medium">Students Registered</th>
                        <th className="border border-gray-300 px-3 py-3 text-center font-medium">Teachers Registered</th>
                        <th className="border border-gray-300 px-3 py-3 text-center font-medium">Progress (%)</th>
                        <th className="border border-gray-300 px-3 py-3 text-right font-medium">Course Price</th>
                        <th className="border border-gray-300 px-3 py-3 text-right font-medium">Discount Offered</th>
                        <th className="border border-gray-300 px-3 py-3 text-right font-medium">Payment Received</th>
                        <th className="border border-gray-300 px-3 py-3 text-center font-medium">Completed Students</th>
                        <th className="border border-gray-300 px-3 py-3 text-center font-medium">Certificates Issued</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dummyCoursesReport.courses.map((course, index) => (
                        <tr key={course.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-3 font-medium">
                            {course.name}
                          </td>
                          <td className="border border-gray-300 px-3 py-3">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                              {course.code}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-center font-semibold text-blue-600">
                            {course.studentsRegistered}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-center font-semibold text-green-600">
                            {course.teachersRegistered}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${getProgressColor(course.progressPercentage)}`}>
                              {course.progressPercentage}%
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-right font-medium">
                            {formatCurrency(course.coursePrice)}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-right text-red-600 font-medium">
                            {formatCurrency(course.discountOffered)}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-right font-bold text-green-600">
                            {formatCurrency(course.paymentReceived)}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-center font-semibold text-purple-600">
                            {course.completedStudents}
                          </td>
                          <td className="border border-gray-300 px-3 py-3 text-center font-semibold text-orange-600">
                            {course.certificatesIssued}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-200 font-bold">
                        <td className="border border-gray-300 px-3 py-3" colspan="2">TOTALS</td>
                        <td className="border border-gray-300 px-3 py-3 text-center text-blue-600">
                          {dummyCoursesReport.summary.totalStudents}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-center text-green-600">
                          {dummyCoursesReport.summary.totalTeachers}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-center">
                          {dummyCoursesReport.summary.averageProgress.toFixed(1)}%
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-right">-</td>
                        <td className="border border-gray-300 px-3 py-3 text-right">-</td>
                        <td className="border border-gray-300 px-3 py-3 text-right text-green-600">
                          {formatCurrency(dummyCoursesReport.summary.totalPaymentReceived)}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-center text-purple-600">
                          {dummyCoursesReport.courses.reduce((sum, course) => sum + course.completedStudents, 0)}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-center text-orange-600">
                          {dummyCoursesReport.summary.totalCertificatesIssued}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No courses found for the selected criteria
                </div>
              )}
            </div>

            {/* Additional Analysis */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3 text-gray-800">Performance Metrics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Completion Rate:</span>
                    <span className="font-medium">
                      {((dummyCoursesReport.courses.reduce((sum, course) => sum + course.completedStudents, 0) / dummyCoursesReport.summary.totalStudents) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Certification Rate:</span>
                    <span className="font-medium">
                      {((dummyCoursesReport.summary.totalCertificatesIssued / dummyCoursesReport.summary.totalStudents) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Average Students per Course:</span>
                    <span className="font-medium">
                      {Math.round(dummyCoursesReport.summary.totalStudents / dummyCoursesReport.courses.length)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3 text-gray-800">Financial Overview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Revenue per Student:</span>
                    <span className="font-medium">
                      {formatCurrency(Math.round(dummyCoursesReport.summary.totalPaymentReceived / dummyCoursesReport.summary.totalStudents))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Discounts Given:</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(dummyCoursesReport.courses.reduce((sum, course) => sum + (course.discountOffered * course.studentsRegistered), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Average Course Price:</span>
                    <span className="font-medium">
                      {formatCurrency(Math.round(dummyCoursesReport.courses.reduce((sum, course) => sum + course.coursePrice, 0) / dummyCoursesReport.courses.length))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-8 space-x-4 border-t pt-4">
          <button 
            onClick={downloadPDF} 
            className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors"
            disabled={loading}
          >
            Download as PDF
          </button>
          <button 
            onClick={downloadExcel} 
            className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors"
            disabled={loading}
          >
            Download as Excel
          </button>
          <button 
            onClick={onClose} 
            className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewCoursesReportModal;